import React, { useEffect, useMemo, useRef, useState } from "react";
import NavBar from "../commonComponents.jsx/NavBar.jsx";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../components/SideBar.jsx";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import FilterPopup from "../commonComponents.jsx/FilterPopup.jsx";
import LocationDropdown from "../commonComponents.jsx/LocationDropdown.jsx";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import CommonFooter from "../commonComponents.jsx/CommonFooter.jsx";
import MultipleLocationSuperMarketModal from "../components/MultipleLocationSuperMarketModal.jsx";
import AIAssistant from "../components/AIAssistant.jsx";
import "highcharts/highcharts-3d";
import down from "../images/address.png";
import noP from "../images/np.png";

ChartJS.register(ArcElement, Tooltip, Legend);

function MonthsDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div ref={ref} className="relative w-48 max-[425px]:w-32">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between px-4 py-2.5 text-sm font-QSemiBold bg-white border rounded-xl shadow-sm transition-all duration-200
          ${open ? "border-indigo-400 ring-2 ring-indigo-200" : "border-gray-200 hover:border-gray-300"}`}
      >
        <span className="text-gray-700">
          {value} {value === 1 ? "Month" : "Months"}
        </span>
        <motion.svg
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </motion.svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute z-20 mt-2 w-full max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg p-1.5 origin-top"
          >
            {months.map((m, i) => (
              <motion.button
                key={m}
                type="button"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                onClick={() => {
                  onChange(m);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors
                  ${
                    value === m
                      ? "bg-indigo-50 text-indigo-600 font-QSemiBold"
                      : "text-gray-600 font-QRegular hover:bg-gray-50"
                  }`}
              >
                {m} {m === 1 ? "Month" : "Months"}
                {value === m && (
                  <svg
                    className="w-4 h-4 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M20 6 9 17l-5-5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Safety % input with animated focus glow + stepper ---------- */
function SafetyPercentInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const val = e.target.value;

    if (val === "") {
      onChange("");
      return;
    }

    const num = Math.min(100, Math.max(0, Number(val)));
    onChange(num);
  };

  const step = (delta) => onChange(Math.min(100, Math.max(0, value + delta)));

  return (
    <motion.div
      animate={{
        boxShadow: focused
          ? "0 0 0 4px rgba(99,102,241,0.15)"
          : "0 1px 2px rgba(0,0,0,0.04)",
      }}
      transition={{ duration: 0.2 }}
      className={`relative w-48 max-[425px]:w-32 flex items-center bg-white border rounded-xl overflow-hidden
        ${focused ? "border-indigo-400" : "border-gray-200"}`}
    >
      <input
        type="number"
        value={value === "" ? "" : Number(value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={handleChange}
        className="w-full px-4 py-2.5 pr-14 text-sm font-QRegular bg-transparent focus:outline-none
          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="pointer-events-none absolute right-11 top-1/2 -translate-y-1/2 text-xs font-QSemiBold text-gray-400">
        %
      </span>
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex flex-col">
        <button
          type="button"
          onClick={() => step(1)}
          className="px-1.5 py-0.5 text-gray-400 hover:text-indigo-600 transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              d="m18 15-6-6-6 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => step(-1)}
          className="px-1.5 py-0.5 text-gray-400 hover:text-indigo-600 transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              d="m6 9 6 6 6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </motion.div>
  );
}

const ReOrder = () => {
  const [isSideBar, setIsSideBar] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [pending, setPending] = useState();
  const [apply, setApply] = useState(0);
  const [productapply, setProductApply] = useState(0);
  const [filterLocations, setFilterLocations] = useState([]);
  const dropdownRef = useRef(null);
  const filterRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [bottomPopup, setBottomPopup] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [months, setMonths] = useState(6); // default matches your sample payload
  const [safetyPercent, setSafetyPercent] = useState(25); // default matches your sample payload

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [Datas, setData] = useState({
    summary: [],
    locationWise: [],
  });
  const [selectedLocationID, setSelectedLocationID] = useState(null);
  const [multipleLocation, setMultipleLocations] = useState([]);

  const [searchType, setSearchType] = useState("name"); // "name" | "barcode"
  const [barcodeQuery, setBarcodeQuery] = useState("");

  const handleLocationSubmit = ({ summary, locationWise, pending }) => {
    setData({ summary, locationWise, pending });
  };

  useEffect(() => {
    const locationID = JSON.parse(sessionStorage.getItem("selectedLocation"));
    const locationList = JSON.parse(
      sessionStorage.getItem("locations") || "[]",
    );
    setSelectedLocationID(locationID);
    setMultipleLocations(locationList);
  }, []);

  const clientid = sessionStorage.getItem("clientId");
  const filteredLocationsId = filterLocations.map((item) => item.locationID);
  const locations = JSON.parse(sessionStorage.getItem("locations") || "[]");

  const adminInfo = JSON.parse(sessionStorage.getItem("adminInfo") || "{}");
  useEffect(() => {
    const hasShownModal = sessionStorage.getItem(
      "hasShownMultipleLocationModal",
    );
    const adminInfo = JSON.parse(sessionStorage.getItem("adminInfo") || "null");

    if (
      !hasShownModal &&
      adminInfo &&
      Array.isArray(adminInfo.multiple_location) &&
      adminInfo.multiple_location.length > 0
    ) {
      setIsLocationModalOpen(true);
      sessionStorage.setItem("hasShownMultipleLocationModal", "true");
    }
  }, []);

  const fetchLocations = async ({ queryKey }) => {
    const [, { clientid, locations }] = queryKey;

    const token = sessionStorage.getItem("token");

    if (!token || !clientid || locations.length === 0) {
      throw new Error("Missing required parameters");
    }

    const requestBody = {
      clientid: clientid,
      dataurl: "/api/Location",
      type: "get",
      subdata: {
        loations: locations,
      },
    };

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/clients/summarydetails`,
      requestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data.result;
  };

  const shouldFetchLocations = adminInfo?.multiple_location?.length === 0;

  const { data: locationData = [] } = useQuery({
    queryKey: ["locations", { clientid, locations }],
    queryFn: fetchLocations,
    enabled: shouldFetchLocations && !!clientid && locations.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const fetchReorderList = async ({ queryKey }) => {
    const [, { clientid }] = queryKey;

    const token = sessionStorage.getItem("token");

    if (!token || !clientid || !fromDate || !toDate) {
      throw new Error("Missing required parameters");
    }

    const locationID =
      Array.isArray(filteredLocationsId) && filteredLocationsId.length > 0
        ? filteredLocationsId[0]
        : locationData[0]?.locationID;
    const asOnDate = new Date().toISOString().split("T")[0];

    const requestBody = {
      clientid: clientid,
      type: "get",
      dataurl: "/api/Products/Reorder",
      subdata: {
        LocationID: locationID,
        AsOnDate: asOnDate,
        Months: months,
        SafetyPercent: safetyPercent,
      },
    };

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/clients/summarydetails`,
      requestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data.result;
  };

  const {
    data: reorderData,
    isLoading,
    isFetching,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ["reorder", { clientid, apply, months, safetyPercent }],
    queryFn: fetchReorderList,
    enabled:
      !!clientid &&
      adminInfo?.multiple_location?.length === 0 &&
      locationData.length > 0 &&
      !!safetyPercent,

    staleTime: 0,
  });

  const fetchMultipleLocations = async ({ queryKey }) => {
    const [, { selectedLocationID, multipleLocation }] = queryKey;

    const token = sessionStorage.getItem("token");

    if (!token || !selectedLocationID || locations.length === 0) {
      throw new Error("Missing required parameters");
    }

    const requestBody = {
      id: selectedLocationID,
      dataurl: "/api/Location",
      type: "get",
      subdata: {
        loations: multipleLocation,
      },
    };

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/clients/multipleLocations-summarydetails`,
      requestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    return response.data.result;
  };

  const { data: multipleLocations = [], refetch: refetchLocation } = useQuery({
    queryKey: ["multi-location-list", { selectedLocationID, locations }],
    queryFn: fetchMultipleLocations,
    enabled:
      !!selectedLocationID &&
      locations.length > 0 &&
      adminInfo?.multiple_location.length >= 1,
    staleTime: 1000 * 60 * 5,
  });

  const locationDatas =
    Datas.summary.length === 0 && Datas.locationWise.length === 0
      ? locationData
      : multipleLocations;

  const clientData = JSON.parse(
    sessionStorage.getItem("clientSettings") || "null",
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isSideBar || isDropdownOpen || isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isSideBar, isDropdownOpen, isFilterOpen]);

  const handleProductClick = (p) => {
    // const sameProducts = reorderData.filter(
    //   (item) => item.productID === p.productID,
    // );

    // if (sameProducts.length > 1) {
    //   setPackPopup({ open: true, options: sameProducts });
    // } else {
    //   setSelectedProductId(p.productID);
    //   setProductApply((prev) => prev + 1);
    //   setSelectedPackId(p.packID);
    // }
    setSelectedProductId(p.productID);
    setProductApply((prev) => prev + 1);
    setSelectedPackId(p.packID);

    setBottomPopup(true);
  };

  const handleApplyFilter = () => {
    setIsFilterOpen(false);
    Datas.summary.length === 0 && Datas.locationWise.length === 0
      ? refetch()
      : refetchLocation();
  };

  const handleResetFilter = () => {
    setFromDate(today);
    setToDate(today);
    setFilterLocations([]);
  };

  const fmt = (val) => val.toFixed(2);

  useEffect(() => {
    if (locationData.length === 0) return;
    setFilterLocations([locationData[0]]);
  }, [locationData]);

  const toggleLocationSelection = (location) => {
    setFilterLocations((prev) => {
      const isAlreadySelected = prev.some(
        (l) => l.locationID === location.locationID,
      );
      return isAlreadySelected ? [] : [location];
    });
  };

  const d = [
    {
      productID: 21,
      product: "RYE GRASS SS - 550 KG",
      barcode: "F200034AE",
      code: "F200034AE",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 4,
      dailyConsumption: 0.022099,
      currentStock: -23,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 23,
    },
    {
      productID: 30,
      product: "WHEAT STRAW + ALFALFA SS - 550 KG",
      barcode: "F200043AE",
      code: "F200043AE",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -11,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 11,
    },
    {
      productID: 32,
      product: "WHEAT STRAW + ALFALFA SS - 650 KG",
      barcode: "F200045AE",
      code: "F200045AE",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -5,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 5,
    },
    {
      productID: 33,
      product: "WHEAT STRAW + ALFALFA SS - 700 KG",
      barcode: "F200046AE",
      code: "F200046AE",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -2,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 2,
    },
    {
      productID: 34,
      product: "WHEAT STRAW + ALFALFA SS - 750 KG",
      barcode: "F200047AE",
      code: "F200047AE",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -8,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 8,
    },
    {
      productID: 44,
      product: "ALFALFA STANDARD GRADE",
      barcode: "F203001FR",
      code: "F203001FR",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 3,
      dailyConsumption: 0.016574,
      currentStock: -11,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 11,
    },
    {
      productID: 52,
      product: "ALFAFLA WHEAT STRAW MIX",
      barcode: "F203008EG",
      code: "F203008EG",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 51,
      dailyConsumption: 0.281767,
      currentStock: -90.025,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 91,
    },
    {
      productID: 53,
      product: "WHEAT STRAW",
      barcode: "F203008RO",
      code: "F203008RO",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -1,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
    {
      productID: 54,
      product: "ALFALFA GRADE 1 40-45 KGS",
      barcode: "F203009EG",
      code: "F203009EG",
      unit: "MT",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 88,
      dailyConsumption: 0.486187,
      currentStock: -23,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 23,
    },
    {
      productID: 55,
      product: "RYE GRASS",
      barcode: "F203009IT",
      code: "F203009IT",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 0.003,
      dailyConsumption: 0.000016,
      currentStock: -0.8234,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
    {
      productID: 56,
      product: "ALFALFA DEHYDRATED PREMIUM BIG BL",
      barcode: "F203009RO",
      code: "F203009RO",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 5,
      dailyConsumption: 0.027624,
      currentStock: -71,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 71,
    },
    {
      productID: 80,
      product: "SUPREME TIMOTHY HAY-T",
      barcode: "0000000000093",
      code: "00107",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 10,
      dailyConsumption: 0.055248,
      currentStock: -15,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 15,
    },
    {
      productID: 88,
      product: "Test33",
      barcode: "0000000000222",
      code: "00114",
      unit: "MT",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "SUPPLYCO",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -1,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
    {
      productID: 89,
      product: "Test_New",
      barcode: "0000000000239",
      code: "00115",
      unit: "T",
      department: "F201U - FORAGE - FINISHED GOODS",
      subDepartment: "F201U - FORAGE - FINISHED GOODS",
      vendor: "SUPPLYCO",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 57,
      dailyConsumption: 0.314917,
      currentStock: -1,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
    {
      productID: 20098,
      product: "9900903 Test",
      barcode: "00903",
      code: "00341",
      unit: "KGS",
      department: "G601U - SERVICES",
      subDepartment: "G601U - SERVICES",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 17,
      dailyConsumption: 0.093922,
      currentStock: -21.001,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 22,
    },
    {
      productID: 30142,
      product: "NIKE/BASKETBALL-CAPS/BLACK/BLACK/(BLACK)/NKFD5181-013-M/L",
      barcode: "197597016641",
      code: "NKFD5181-013-M/L",
      unit: "PCS",
      department: "ACCESSORIES",
      subDepartment: "BASKETBALL-CAPS",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -1,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
    {
      productID: 30230,
      product:
        "NIKE/SPORTSWEAR-LACED SHOES/REGALPINKPINKFOAM-SOFTPEARL/NKHJ5870-600-9.5",
      barcode: "197862741537",
      code: "NKHJ5870-600-9.5",
      unit: "Pairs",
      department: "FOOTWEAR",
      subDepartment: "SPORTSWEAR-LACED SHOES",
      vendor: "",
      salesPerson: "",
      phone: "",
      mobile: "",
      assignedReOrderLvl: 0,
      location: "U1E01 - AL FAYA - POS 1",
      totalConsumption: 1,
      dailyConsumption: 0.005524,
      currentStock: -1,
      leadDays: 0,
      safetyStock: 0,
      reOrderQty: 1,
    },
  ];

  const getDisplayText = (locations) => {
    if (locations.length === 0) {
      return "All Locations";
    }

    return locations.length > 2
      ? `${locations[0].locationName}, +${locations.length - 1} more`
      : locations.map((location) => location.locationName).join(", ");
  };

  return (
    <>
      <div className="min-h-[100dvh] bg-gray-50 overflow-hidden">
        <div className="max-w-[768px] mx-auto relative">
          <AnimatePresence>
            {isSideBar && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="fixed inset-0 bg-black z-50 "
                  onClick={() => setIsSideBar(false)}
                />
                <motion.div
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                    duration: 0.3,
                  }}
                  className="absolute w-80 h-full overflow-hidden overflow-y-auto z-50 bg-white"
                >
                  <Sidebar
                    onHide={() => setIsSideBar(false)}
                    page={"Reorder"}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>

          <div className="bg-[#F3F3F3] rounded-3xl overflow-y-auto shadow-sm p-4 h-[100dvh]">
            <NavBar onSideBar={() => setIsSideBar(true)} />
            <div className="flex justify-between items-center mb-4">
              <div className="relative inline-block ">
                <div
                  className="
                                      bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 border border-indigo-100
                                      shadow-sm shadow-indigo-100
                                      transition-all duration-300
                                      overflow-hidden
                                  "
                >
                  <h1
                    className="
                                      text-sm 
                                      text-indigo-600
                                      relative z-10
                                      transition-all duration-300
                                      font-QBold
                                      "
                  >
                    Reorder List
                  </h1>
                </div>
              </div>
              {/* <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-1 bg-indigo-100 rounded-[8px] px-3 py-2 shadow-sm hover:bg-indigo-200 transition-colors"
              >
                <i className="ri-filter-line text-indigo-600"></i>
                <span className="text-sm font-QSemiBold text-indigo-700">
                  Filter
                </span>
              </button> */}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
              {/* Search Bar */}

              {/* Location Selector */}
              {adminInfo?.multiple_location &&
              adminInfo.multiple_location.length > 0 ? (
                <button
                  onClick={() => setIsLocationModalOpen(true)}
                  className="px-4 py-2.5 flex items-center gap-x-2 bg-indigo-700 rounded-xl text-white text-sm font-QMedium tracking-tight whitespace-nowrap shadow-sm hover:bg-indigo-800 transition"
                >
                  Locations
                  <img src={down} className="w-4 h-4" alt="" />
                </button>
              ) : (
                <LocationDropdown
                  dropdownRef={dropdownRef}
                  isDropdownOpen={isDropdownOpen}
                  setIsDropdownOpen={setIsDropdownOpen}
                  filterLocations={filterLocations}
                  locations={locationData}
                  toggleLocationSelection={toggleLocationSelection}
                  getDisplayText={getDisplayText}
                  onApply={() => refetch()}
                />
              )}
              {/* Months + Safety % */}
              <div className="flex items-end justify-between gap-4 mt-1">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-QSemiBold text-gray-500">
                    Months
                  </label>
                  <MonthsDropdown value={months} onChange={setMonths} />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-QSemiBold text-gray-500">
                    Safety %
                  </label>
                  <SafetyPercentInput
                    value={safetyPercent}
                    onChange={setSafetyPercent}
                  />
                </div>
              </div>
            </div>

            {/* <div>
              <FilterPopup
                ref={filterRef}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                locations={locationDatas}
                selectedLocations={filterLocations}
                toggleLocationSelection={toggleLocationSelection}
                getDisplayText={getDisplayText}
                onReset={handleResetFilter}
                onApply={handleApplyFilter}
              />
            </div> */}

            {/* Replace your grid section with this */}
            <AnimatePresence mode="wait">
              {/* Loading State */}
              {isLoading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 mt-4 sm:grid-cols-2 gap-4"
                >
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-3"
                    >
                      {/* Top Row Skeleton */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 animate-pulse flex-shrink-0" />
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="h-3.5 bg-gray-100 animate-pulse rounded-full w-4/5" />
                          <div className="h-3 bg-gray-100 animate-pulse rounded-full w-2/5" />
                        </div>
                      </div>
                      {/* Badges Skeleton */}
                      <div className="flex gap-2">
                        <div className="h-6 w-20 bg-gray-100 animate-pulse rounded-full" />
                        <div className="h-6 w-16 bg-gray-100 animate-pulse rounded-full" />
                      </div>
                      {/* Price Skeleton */}
                      <div className="flex gap-6 border-t border-gray-100 pt-3">
                        <div className="flex flex-col gap-1">
                          <div className="h-2.5 w-8 bg-gray-100 animate-pulse rounded-full" />
                          <div className="h-3.5 w-12 bg-gray-100 animate-pulse rounded-full" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="h-2.5 w-8 bg-gray-100 animate-pulse rounded-full" />
                          <div className="h-3.5 w-12 bg-gray-100 animate-pulse rounded-full" />
                        </div>
                        <div className="flex flex-col gap-1 ml-auto items-end">
                          <div className="h-2.5 w-14 bg-gray-100 animate-pulse rounded-full" />
                          <div className="h-3.5 w-12 bg-gray-100 animate-pulse rounded-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Empty State */}
              {!isLoading &&
                !isFetching &&
                isFetched &&
                (!reorderData || reorderData.length === 0) && (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center justify-center py-16 gap-4"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#d1d5db"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M8 3h8l1 7c0 3.3-2.2 6-5 6s-5-2.7-5-6l1-7z" />
                        <line x1="12" y1="16" x2="12" y2="21" />
                        <line x1="9" y1="21" x2="15" y2="21" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-QSemiBold text-gray-500">
                        No products found
                      </p>
                      <p className="text-xs text-gray-400 mt-1 font-QMedium">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </motion.div>
                )}

              {/* Product Grid */}
              {!isLoading && reorderData && reorderData.length > 0 && (
                <motion.div
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 mt-4 sm:grid-cols-2 gap-4"
                >
                  {reorderData.map((p, i) => {
                    const isNegativeStock = p.currentStock < 0;

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: i * 0.05,
                          type: "spring",
                          stiffness: 280,
                          damping: 24,
                        }}
                        onClick={() => handleProductClick(p)}
                        className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-3 hover:border-gray-400 hover:shadow-sm transition-all duration-200 cursor-default"
                      >
                        {/* Top Row */}
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#4338ca"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M8 3h8l1 7c0 3.3-2.2 6-5 6s-5-2.7-5-6l1-7z" />
                              <line x1="12" y1="16" x2="12" y2="21" />
                              <line x1="9" y1="21" x2="15" y2="21" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-QSemiBold text-gray-900 leading-snug italic line-clamp-2">
                              {p.product}
                            </p>
                            <p className="text-xs font-QMedium text-gray-400 mt-1 tracking-wider">
                              {p.barcode} · ID {p.productID}
                            </p>
                          </div>
                        </div>

                        {/* Stats Row */}
                        {/* Stats Row */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 border-t border-gray-100 pt-3">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-[10px] sm:text-xs uppercase tracking-wide sm:tracking-widest text-gray-400 font-QSemiBold leading-tight">
                              Total Consumption
                            </span>
                            <span className="text-xs font-QMedium text-gray-700 truncate">
                              {p.totalConsumption}
                            </span>
                          </div>
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-[10px] sm:text-xs uppercase tracking-wide sm:tracking-widest text-gray-400 font-QSemiBold leading-tight">
                              Daily Consumption
                            </span>
                            <span className="text-xs font-QMedium text-gray-700 truncate">
                              {p.dailyConsumption.toFixed(4)}
                            </span>
                          </div>
                          <div className="flex flex-col gap-0.5 min-w-0 text-right">
                            <span className="text-[10px] sm:text-xs uppercase tracking-wide sm:tracking-widest text-gray-400 font-QSemiBold leading-tight">
                              Current Stock
                            </span>
                            <span
                              className={`text-xs font-QMedium truncate ${
                                isNegativeStock
                                  ? "text-red-600"
                                  : "text-gray-700"
                              }`}
                            >
                              {p.currentStock}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="h-20"></div>

            {/* Bottom Nav */}
            <CommonFooter page={"Department wise Sales"} />
          </div>
        </div>
        <MultipleLocationSuperMarketModal
          isOpen={isLocationModalOpen}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setIsLocationModalOpen(false)}
          onDataSubmit={handleLocationSubmit}
          dataUrl={"Sales/DepartmentwiseAmount"}
          onPendingChange={setPending}
        />
      </div>
      <AIAssistant />
    </>
  );
};

export default ReOrder;
